// CustomTable.tsx - Updated version
import React, { Dispatch, SetStateAction, useState, useEffect, useMemo, useCallback } from "react";
import TypoGraphyKit from "../../kit/typoGraphy";
import { Checkbox, Pagination, PaginationItem, Tooltip, CircularProgress } from "@mui/material"; // Added CircularProgress
import { toPersianNumber } from "@/hooks/useThousandsSeparator";
import StackKit from "../../kit/stack";

type TableProps = {
  dataHeader: { id: number | string; title: string }[];
  dataTbody:
    | {
        [key: string]: string | number | React.ReactNode | undefined;
        id?: number;
        moreElement?: React.ReactNode;
      }[]
    | any[];
  colorPagination?: string;
  total?: number;
  setPages?: Dispatch<SetStateAction<number>>;
  currentPage?: number;
  itemsPerPage?: number;
  // New props
  selectable?: boolean;
  selectedRows?: number[];
  onSelectionChange?: (selectedIds: number[]) => void;
  bulkSelection?: boolean;
  onBulkSelectionChange?: (selectedAll: boolean) => void;
  isLoading?: boolean; // Added loading prop
};

const CustomTable = ({
  dataHeader,
  dataTbody,
  colorPagination = "#1976d2",
  total = 0,
  setPages,
  currentPage = 1,
  itemsPerPage = 10,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  bulkSelection = false,
  onBulkSelectionChange,
  isLoading = false, // Default false
}: TableProps) => {
  // Use selectedRows as initial state, but manage locally
  const [localSelected, setLocalSelected] = useState<number[]>(selectedRows);

  // FIX: Add proper comparison to prevent infinite updates
  useEffect(() => {
    if (JSON.stringify(selectedRows) !== JSON.stringify(localSelected)) {
      setLocalSelected(selectedRows);
    }
  }, [selectedRows]);

  const memoizedDataTbody = useMemo(() => dataTbody || [], [dataTbody]);

  const allIds = useMemo(() =>
    (memoizedDataTbody || [])
      .map(row => row?.id)
      .filter((id): id is number => id !== undefined && typeof id === 'number'),
    [memoizedDataTbody]
  );

  const handleSelectRow = useCallback((rowId: number) => {
    const newSelected = localSelected.includes(rowId)
      ? localSelected.filter(id => id !== rowId)
      : [...localSelected, rowId];

    setLocalSelected(newSelected);
    onSelectionChange?.(newSelected);
  }, [localSelected, onSelectionChange]);

  const handleSelectAll = useCallback(() => {
    if (allIds.length === 0) return;

    const isAllCurrentlySelected = allIds.length > 0 &&
      allIds.every(id => localSelected.includes(id));

    let newSelected: number[];

    if (isAllCurrentlySelected) {
      newSelected = localSelected.filter(id => !allIds.includes(id));
    } else {
      const combinedSet = new Set([...localSelected, ...allIds]);
      newSelected = Array.from(combinedSet);
    }

    setLocalSelected(newSelected);
    onSelectionChange?.(newSelected);

    if (bulkSelection && onBulkSelectionChange) {
      onBulkSelectionChange(!isAllCurrentlySelected);
    }
  }, [allIds, localSelected, onSelectionChange, bulkSelection, onBulkSelectionChange]);

  const isAllSelected = useMemo(() =>
    allIds.length > 0 && allIds.every(id => localSelected.includes(id)),
    [allIds, localSelected]
  );

  const handlePageChange = useCallback((_: any, page: number) => {
    if (setPages && !isLoading) { // Prevent page change while loading
      setPages(page);
    }
  }, [setPages, isLoading]);

  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div style={{ padding: 16, width: "100%", position: 'relative' }}>
      {/* Loading Overlay */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          borderRadius: '8px'
        }}>
          <CircularProgress sx={{ color: '#fff' }} />
        </div>
      )}

      <table className="border-separate border-spacing-x-3 border-spacing-y-3 table-auto" style={{ width: "100%", opacity: isLoading ? 0.5 : 1 }}>
        <thead>
          <tr className="text-[#A4A4A4]">
            {selectable && (
              <th className="text-center">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={localSelected.length > 0 && !isAllSelected}
                  onChange={handleSelectAll}
                  disabled={isLoading}
                  sx={{
                    color: '#fff',
                    '&.Mui-checked': {
                      color: colorPagination,
                    },
                  }}
                />
              </th>
            )}

            {dataHeader?.map((header) => (
              <th key={header.id} className="text-center">
                <TypoGraphyKit variant="body1">{header.title}</TypoGraphyKit>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {memoizedDataTbody.length === 0 ? (
            <tr>
              <td
                colSpan={dataHeader.length + (selectable ? 1 : 0)}
                className="text-center py-8 text-gray-400"
              >
                دیتایی وجود ندارد
              </td>
            </tr>
          ) : (
            memoizedDataTbody.map((row, rowIndex) => {
              const rowClassName = row?.rowClassName || "";
              const cellClassNames = row?.cellClassNames || {};
              const rowId = row?.id ?? rowIndex;

              return (
                <tr key={rowId} className={`text-center bg-transparent text-white ${rowClassName}`} style={{ borderRadius: "8px", overflow: "hidden" }}>
                  {selectable && (
                    <td className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#fff] to-[#EAEAEAFF]">
                      <Checkbox
                        checked={localSelected.includes(rowId)}
                        onChange={() => handleSelectRow(rowId)}
                        disabled={isLoading}
                        sx={{
                          color: '#fff',
                          '&.Mui-checked': {
                            color: colorPagination,
                          },
                        }}
                      />
                    </td>
                  )}

                  {dataHeader?.map((header) => {
                    const cellText = row?.[header.id] ?? "-";
                    return (
                      <td
                        key={header.id}
                        className={`px-4 py-2 ${
                          header.id === "moreElement"
                            ? "!bg-transparent !bg-none"
                            : Array.isArray(row?.__rawCells__) && row?.__rawCells__?.includes(header.id)
                            ? ""
                            : "rounded-lg bg-gradient-to-r from-[#fff] to-[#EAEAEAFF]"
                        }  ${cellClassNames[header.id]}`}
                      >
                        {header.id !== "moreElement" ? (
                          <Tooltip title={cellText ?? "-"} placement="top" arrow>
                            <span style={{ display: "inline-block", maxWidth: "200px" }}>
                              <TypoGraphyKit
                                variant="body1"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: "vertical",
                                  cursor: "pointer",
                                  maxWidth: "200px",
                                }}
                              >
                                {cellText ?? "-"}
                              </TypoGraphyKit>
                            </span>
                          </Tooltip>
                        ) : (
                          <TypoGraphyKit
                            variant="body1"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: "vertical",
                              cursor: "pointer",
                              maxWidth: "200px",
                            }}
                          >
                            {cellText ?? "-"}
                          </TypoGraphyKit>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <StackKit sx={{ width: "100%", mt: 2 }} justifyContent={"center"} alignItems={"center"}>
          <Pagination
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              "&.MuiPagination-ul": {
                justifyContent: 'center'
              }
            }}
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            disabled={isLoading}
            renderItem={(item) => (
              <PaginationItem
                {...item}
                disabled={isLoading}
                sx={{
                  "&.Mui-selected": {
                    color: colorPagination,
                    fontWeight: "bold",
                    borderRadius: "8px"
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5
                  }
                }}
              >
                {typeof item.page === "number" ? toPersianNumber(item.page) : item.page}
              </PaginationItem>
            )}
          />
        </StackKit>
      )}
    </div>
  );
};

export default React.memo(CustomTable);
